// Histórico da Gestão em Lote com diff visual + Rollback protegido
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  ClipboardList, ArrowRight, Undo2, ShieldAlert, ChevronLeft, Search, AlertTriangle,
} from "lucide-react";

const nfmt = (n) => (n ?? 0).toLocaleString("pt-BR");

export default function BulkHistoryPage() {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null); // {op, changes: []}
  const [q, setQ] = useState("");
  const [rollbackTarget, setRollbackTarget] = useState(null); // { batch_id, preview }

  useEffect(() => {
    api.get("/bulk/audit-log")
      .then(({ data }) => setItems(data.rows || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const filtered = (items || []).filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [r.batch_id, r.user?.email, r.user?.name, r.linked_batch_id,
            ...(r.per_field_result || []).map((p) => p.field)]
      .filter(Boolean)
      .join(" ").toLowerCase().includes(s);
  });

  const openDetail = async (batch_id) => {
    try {
      const { data } = await api.get(`/bulk/operations/${batch_id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startRollback = async (batch_id) => {
    try {
      const { data } = await api.post(`/bulk/rollback/${batch_id}/preview`);
      setRollbackTarget({ batch_id, preview: data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4 pb-8" data-testid="bulk-history-page">
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8A6B2B] font-semibold">Administração · Gestão em Lote</div>
          <h1 className="font-display text-[22px] font-semibold text-slate-900 leading-tight flex items-center gap-2">
            <ClipboardList className="h-5 w-5" /> Histórico de operações
          </h1>
        </div>
        <Button variant="outline" asChild className="h-9 border-slate-300">
          <Link to="/gestao-em-lote"><ChevronLeft className="h-4 w-4 mr-1" /> Voltar</Link>
        </Button>
      </header>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Buscar por batch, usuário, campo..."
                 className="pl-7 h-9 text-[12.5px]" data-testid="history-search" />
        </div>
        <span className="text-[11px] text-slate-500">{filtered.length} operação(ões)</span>
      </div>

      {!items ? (
        <Skeleton className="h-40" />
      ) : filtered.length === 0 ? (
        <div className="text-center text-[13px] text-slate-500 border border-dashed border-slate-200 rounded-lg p-10">
          Nenhuma operação encontrada.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Batch</th>
                <th className="text-left px-3 py-2">Data/Hora</th>
                <th className="text-left px-3 py-2">Usuário</th>
                <th className="text-left px-3 py-2">Alvos</th>
                <th className="text-left px-3 py-2">Campos</th>
                <th className="text-right px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((op) => (
                <tr key={op.batch_id} className="border-t border-slate-100 hover:bg-slate-50/60"
                    data-testid={`row-${op.batch_id}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    {op.batch_id.slice(0, 8)}
                    {op.kind === "rollback" && (
                      <span className="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] uppercase font-semibold">rollback</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{new Date(op.started_at).toLocaleString("pt-BR")}</td>
                  <td className="px-3 py-2 text-slate-700">{op.user?.email}</td>
                  <td className="px-3 py-2 tabular-nums">{nfmt(op.target_ids_count || op.per_field_result?.reduce((s, p) => s + (p.modified || 0), 0))}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(op.per_field_result || []).map((p) => (
                        <span key={p.field} className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px]">
                          {p.field} · {p.modified ?? p.matched}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openDetail(op.batch_id)}
                            className="h-7 text-[11px]" data-testid={`btn-detail-${op.batch_id}`}>
                      Ver diff
                    </Button>
                    {op.kind !== "rollback" && (
                      <Button size="sm" variant="ghost" onClick={() => startRollback(op.batch_id)}
                              className="h-7 text-[11px] text-amber-700 hover:bg-amber-50"
                              data-testid={`btn-rollback-${op.batch_id}`}>
                        <Undo2 className="h-3 w-3 mr-1" /> Reverter
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal com diff */}
      {detail && (
        <Dialog open onOpenChange={(v) => !v && setDetail(null)}>
          <DialogContent className="max-w-3xl" data-testid="detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4" />
                Diff da operação · {detail.batch_id?.slice(0, 8)}
              </DialogTitle>
              <DialogDescription>
                {new Date(detail.started_at).toLocaleString("pt-BR")} · {detail.user?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[65vh] overflow-y-auto">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-2 text-[11.5px]">
                Alvos: <b>{nfmt(detail.target_ids_count)}</b> · Fora do escopo: <b>{detail.out_of_scope || 0}</b> · Alterações registradas: <b>{detail.sample_changes?.length || 0}</b>
              </div>
              {(detail.sample_changes || []).length === 0 && (
                <div className="text-[12px] text-slate-500">Nenhuma alteração detalhada disponível.</div>
              )}
              {(() => {
                // Agrupa por employee
                const grouped = {};
                (detail.sample_changes || []).forEach((c) => {
                  const k = c.employee_id;
                  if (!grouped[k]) grouped[k] = { matricula: c.matricula, nome: c.nome, changes: [] };
                  grouped[k].changes.push(c);
                });
                return Object.entries(grouped).slice(0, 100).map(([eid, g]) => (
                  <div key={eid} className="rounded-md border border-slate-200 p-2">
                    <div className="text-[12px] font-semibold text-slate-800">
                      {g.nome || "(sem nome)"} <span className="font-mono text-[11px] text-slate-500 ml-1">· {g.matricula || eid.slice(0, 8)}</span>
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {g.changes.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-[11.5px]">
                          <span className="font-mono text-slate-600 w-40 shrink-0">{c.field}</span>
                          <span className="text-rose-600 line-through max-w-[35%] truncate">{c.before ?? <i>vazio</i>}</span>
                          <ArrowRight className="h-3 w-3 text-slate-400" />
                          <span className="text-emerald-700 max-w-[35%] truncate">{c.after ?? <i>vazio</i>}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDetail(null)}>Fechar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Rollback modal */}
      {rollbackTarget && (
        <RollbackDialog target={rollbackTarget}
                        onClose={() => setRollbackTarget(null)}
                        onDone={() => { setRollbackTarget(null); window.location.reload(); }} />
      )}
    </div>
  );
}

function RollbackDialog({ target, onClose, onDone }) {
  const [text, setText] = useState("");
  const [applying, setApplying] = useState(false);
  const short = target.batch_id.slice(0, 8).toUpperCase();
  const expected = `REVERTER BATCH ${short}`;
  const matched = text.trim().toUpperCase() === expected.toUpperCase();
  const pv = target.preview;

  const doIt = async () => {
    if (!matched) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/bulk/rollback/${target.batch_id}`,
                                      { confirmation_text: text.trim() });
      toast.success(`Rollback executado. ${data.reverted_total} alteração(ões) revertida(s).`);
      onDone();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message);
      } else toast.error(formatApiError(detail));
    } finally { setApplying(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="rollback-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-800 flex items-center gap-2">
            <Undo2 className="h-4 w-4" /> Reverter operação · {short}
          </DialogTitle>
          <DialogDescription>
            Revisar antes de reverter. Alterações posteriores são preservadas.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[12px] space-y-1">
            <div>Total de alterações no batch: <b>{pv.total_changes}</b></div>
            <div>Fora do seu escopo (não visíveis): <b>{pv.out_of_scope}</b></div>
            <div className="text-emerald-700">Podem ser revertidas: <b>{pv.revertible}</b></div>
            <div className="text-amber-700">Com conflito (alterados depois): <b>{pv.conflicts}</b></div>
          </div>
          {pv.conflicts > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11.5px] text-amber-900">
              <div className="font-semibold flex items-center gap-1 mb-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Registros com conflito (não serão revertidos)
              </div>
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {pv.conflicts_sample.slice(0, 8).map((c, i) => (
                  <div key={i} className="font-mono text-[11px]">
                    {c.field}: esperava "{String(c.after ?? "—")}" mas está "{String(c.current ?? "—")}"
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-3 text-[12px]">
            Para confirmar, digite: <span className="font-mono text-[11.5px]">{expected}</span>
            <Input autoFocus value={text} onChange={(e) => setText(e.target.value)}
                   placeholder={expected}
                   className={"mt-2 " + (matched ? "border-emerald-400" : "")}
                   data-testid="rollback-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={applying}>Cancelar</Button>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={applying || !matched || pv.revertible === 0}
                  onClick={doIt} data-testid="btn-rollback-confirm">
            {applying ? "Revertendo..." : `Reverter ${pv.revertible} alteração(ões)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
